import type { ResolvedTheme, ThemePreference } from "@/stores/use-theme-store";
import { useThemeStore } from "@/stores/use-theme-store";

const DARK_CLASS = "dark";
const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

/* Keeps the mobile browser chrome in step with the page background. */
/* Must track --color-canvas in src/index.css, or the browser chrome jumps. */
const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: "#f4f7fb",
  dark: "#101215",
};

const getSystemTheme = (): ResolvedTheme => {
  return window.matchMedia(DARK_MEDIA_QUERY).matches ? "dark" : "light";
};

export const resolveTheme = (preference: ThemePreference): ResolvedTheme => {
  return preference === "system" ? getSystemTheme() : preference;
};

const applyTheme = (preference: ThemePreference) => {
  const resolvedTheme = resolveTheme(preference);
  const root = document.documentElement;

  root.classList.toggle(DARK_CLASS, resolvedTheme === "dark");
  root.style.colorScheme = resolvedTheme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLORS[resolvedTheme]);

  if (useThemeStore.getState().resolvedTheme !== resolvedTheme) {
    useThemeStore.getState().setResolvedTheme(resolvedTheme);
  }
};

export const initializeTheme = () => {
  let lastPreference = useThemeStore.getState().preference;
  applyTheme(lastPreference);

  useThemeStore.subscribe((state) => {
    if (state.preference === lastPreference) {
      return;
    }
    lastPreference = state.preference;
    applyTheme(lastPreference);
  });

  window.matchMedia(DARK_MEDIA_QUERY).addEventListener("change", () => {
    if (useThemeStore.getState().preference === "system") {
      applyTheme("system");
    }
  });
};
